import { PublicodesExpression } from 'publicodes'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import RuleLink from '@/components/RuleLink'
import { Lodeom } from '@/contextes/salarié'
import { baseTheme, H3, Spacing } from '@/design-system'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import useYear from '@/hooks/useYear'
import { useZoneLodeom } from '@/hooks/useZoneLodeom'

import RécapitulatifTrimestre from './RécapitulatifTrimestre'
import RéductionMois from './RéductionMois'

type Props = {
	data: Lodeom.MonthState[]
	onRémunérationChange: (monthIndex: number, rémunérationBrute: number) => void
	onOptionsChange: (monthIndex: number, options: Lodeom.Options) => void
	caption: string
	warningCondition: PublicodesExpression
	warningTooltip: ReactNode
	warnings?: ReactNode
	codeRéduction?: string
	codeRégularisation?: string
	withRépartitionAndRégularisation?: boolean
}

export default function RéductionMoisParMois({
	data,
	onRémunérationChange,
	onOptionsChange,
	caption,
	warningCondition,
	warningTooltip,
	warnings,
	codeRéduction,
	codeRégularisation,
	withRépartitionAndRégularisation = true,
}: Props) {
	const currentZone = useZoneLodeom()
	const year = useYear()
	// La Lodeom ne s'applique à Mayotte qu'à partir du 1er juillet 2026
	const masquerPremierSemestre = currentZone === 'mayotte' && year === 2026
	const premierMoisAffiché = masquerPremierSemestre ? 6 : 0
	const { t } = useTranslation()
	const isDesktop = useMediaQuery(
		`(min-width: ${baseTheme.breakpointsWidth.md})`
	)

	const mois = [
		t('global.mois.janvier', 'janvier'),
		t('global.mois.février', 'février'),
		t('global.mois.mars', 'mars'),
		t('global.mois.avril', 'avril'),
		t('global.mois.mai', 'mai'),
		t('global.mois.juin', 'juin'),
		t('global.mois.juillet', 'juillet'),
		t('global.mois.août', 'août'),
		t('global.mois.septembre', 'septembre'),
		t('global.mois.octobre', 'octobre'),
		t('global.mois.novembre', 'novembre'),
		t('global.mois.décembre', 'décembre'),
	]
	const moisÀAfficher = mois.slice(premierMoisAffiché)

	const trimestres = masquerPremierSemestre
		? {
				[t('pages.simulateurs.lodeom.recap.T3', '3ème trimestre')]: data.slice(
					6,
					9
				),
				[t('pages.simulateurs.lodeom.recap.T4', '4ème trimestre')]:
					data.slice(9),
			}
		: {
				[t('pages.simulateurs.lodeom.recap.T1', '1er trimestre')]: data.slice(
					0,
					3
				),
				[t('pages.simulateurs.lodeom.recap.T2', '2ème trimestre')]: data.slice(
					3,
					6
				),
				[t('pages.simulateurs.lodeom.recap.T3', '3ème trimestre')]: data.slice(
					6,
					9
				),
				[t('pages.simulateurs.lodeom.recap.T4', '4ème trimestre')]:
					data.slice(9),
			}

	return (
		<>
			{isDesktop ? (
				<>
					<H3 as="h2">{caption}</H3>
					<StyledTable>
						<caption className="sr-only">{caption}</caption>
						<thead>
							<tr>
								<th scope="col">{t('Mois')}</th>
								<th scope="col">
									<RuleLink dottedName={Lodeom.rémunérationBruteDottedName} />
								</th>
								<th scope="col">
									<RuleLink dottedName={Lodeom.lodeomDottedName} />
								</th>
								{withRépartitionAndRégularisation && (
									<th scope="col">
										<RuleLink dottedName="salarié . cotisations . exonérations . lodeom . régularisation" />
									</th>
								)}
							</tr>
						</thead>
						<tbody>
							{data.length > 0 &&
								moisÀAfficher.map((monthName, index) => {
									const monthIndex = index + premierMoisAffiché

									return (
										<RéductionMois
											key={`month-${monthIndex}`}
											monthName={monthName}
											data={data[monthIndex]}
											index={monthIndex}
											onRémunérationChange={onRémunérationChange}
											onOptionsChange={onOptionsChange}
											warningCondition={warningCondition}
											warningTooltip={warningTooltip}
											withRépartitionAndRégularisation={
												withRépartitionAndRégularisation
											}
										/>
									)
								})}
						</tbody>
					</StyledTable>

					<Spacing md />

					<H3 as="h2">
						{t(
							'pages.simulateurs.lodeom.recap.caption',
							'Récapitulatif trimestriel :'
						)}
					</H3>
					<StyledRecapTable>
						<caption className="sr-only">
							{t(
								'pages.simulateurs.lodeom.recap.caption',
								'Récapitulatif trimestriel :'
							)}
						</caption>
						<thead>
							<tr>
								<th scope="col">{t('Trimestre')}</th>
								<th scope="col">
									{t(
										'pages.simulateurs.lodeom.recap.header-réduction',
										'Réduction calculée'
									)}
									{codeRéduction && (
										<>
											<br />
											{codeRéduction}
										</>
									)}
								</th>
								{withRépartitionAndRégularisation && (
									<th scope="col">
										{t(
											'pages.simulateurs.lodeom.recap.header-régularisation',
											'Régularisation calculée'
										)}
										{codeRégularisation && (
											<>
												<br />
												{codeRégularisation}
											</>
										)}
									</th>
								)}
							</tr>
						</thead>
						<tbody>
							{Object.keys(trimestres).map((label, index) => (
								<RécapitulatifTrimestre
									key={index}
									label={label}
									data={trimestres[label]}
									codeRéduction={codeRéduction}
									codeRégularisation={codeRégularisation}
									withRépartitionAndRégularisation={
										withRépartitionAndRégularisation
									}
								/>
							))}
						</tbody>
					</StyledRecapTable>
				</>
			) : (
				<>
					<H3 as="h2">{caption}</H3>

					{data.length > 0 &&
						moisÀAfficher.map((monthName, index) => {
							const monthIndex = index + premierMoisAffiché

							return (
								<RéductionMois
									key={`month-${monthIndex}`}
									monthName={monthName}
									data={data[monthIndex]}
									index={monthIndex}
									onRémunérationChange={onRémunérationChange}
									onOptionsChange={onOptionsChange}
									warningCondition={warningCondition}
									warningTooltip={warningTooltip}
									withRépartitionAndRégularisation={
										withRépartitionAndRégularisation
									}
									mobileVersion={true}
								/>
							)
						})}

					<Spacing xxl />

					<H3 as="h2">
						{t(
							'pages.simulateurs.lodeom.recap.caption',
							'Récapitulatif trimestriel :'
						)}
					</H3>
					{Object.keys(trimestres).map((label, index) => (
						<RécapitulatifTrimestre
							key={index}
							label={label}
							data={trimestres[label]}
							codeRéduction={codeRéduction}
							codeRégularisation={codeRégularisation}
							withRépartitionAndRégularisation={
								withRépartitionAndRégularisation
							}
							mobileVersion={true}
						/>
					))}
				</>
			)}

			<span id="options-description" className="sr-only">
				{t(
					'pages.simulateurs.lodeom.options.description',
					"Ajoute des champs pour moduler l'activité du salarié"
				)}
			</span>

			{warnings && (
				<>
					<Spacing xxl />
					{warnings}
				</>
			)}
		</>
	)
}

const StyledTable = styled.table`
	border-collapse: collapse;
	text-align: left;
	width: 100%;
	color: ${({ theme }) => theme.colors.bases.primary[100]};
	font-family: ${({ theme }) => theme.fonts.main};
	caption {
		text-align: left;
		margin: ${({ theme }) => `${theme.spacings.sm} 0 `};
	}
	th,
	td {
		padding: ${({ theme }) => theme.spacings.xs};
	}
	th:not(:first-of-type) {
		text-align: right;
	}
	tbody tr th {
		text-transform: capitalize;
		font-weight: normal;
	}
`
const StyledRecapTable = styled(StyledTable)`
	thead {
		border-bottom: solid 1px;
	}
	thead th:not(:last-of-type),
	tbody th,
	td:not(:last-of-type) {
		border-right: solid 1px;
	}
	thead th:not(:first-of-type) {
		text-align: center;
	}
`
